import { Space, Typography } from 'antd';
import { FormikProvider, useFormik } from 'formik';
import { useMemo, useState } from 'react';
import { SettingOutlined } from '@ant-design/icons';
import { H3 } from 'components/new-ui/base/typography/styles';
import CardWithIcon from 'components/new-ui/base/card/CardWithIcon';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import FormNumberInput from 'components/new-ui/composed/formInput/FormNumberInput';
import { InvoiceConfigDueDateAlertUnit, UpdateInvoiceConfigGeneralSettingsPayload } from 'store/invoice-config/types';
import FormSelect from 'components/new-ui/composed/formSelect/FormSelect';
import { useRequestPromise } from 'hooks/useRequest';
import { listAdditionalCharges } from 'store/invoice-config/client';
import Button from 'components/new-ui/base/button/Button';
import { ChargeSelectOption } from 'views/invoices/invoice-details/components/additional-charges/SelectChargeModal';
import useDebouncedValue from 'hooks/useDebouncedValue';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import Notification from 'components/ui/base/notification/Notification';
import DueDateAlertsField from './DueDateAlertsField';
import { generalSettingsValidationSchema } from './validationSchema';
import useInvoicesConfig from 'hooks/useInvoicesConfig';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { useSyncDirty } from 'views/invoices/settings/tabs/GeneralSettingsDirtyContext';
import { MAX_INPUT_WIDTH } from '../../utils';
import AdditionalChargeLabel from './AdditionalChargeLabel';

const GeneralSettingsSection = () => {
    const { config, updateGeneralSettings } = useInvoicesConfig();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [query, setQuery] = useState<string>();
    const debouncedQuery = useDebouncedValue(query);

    const { data } = useRequestPromise(
        () => listAdditionalCharges({ query: debouncedQuery, archived: false }),
        undefined,
        [debouncedQuery]
    );

    const formik = useFormik({
        enableReinitialize: true,
        validationSchema: generalSettingsValidationSchema,
        initialValues: {
            invoiceDueDays: config?.invoiceDueDays ?? null,
            // The BE always expects the unit
            dueDateAlert: config?.dueDateAlert ?? {
                unit: InvoiceConfigDueDateAlertUnit.DAYS,
                amount: null,
            },
            additionalChargeId: config?.additionalCharge?.id ?? null,
        },
        onSubmit: async (values) => {
            try {
                const { invoiceDueDays, dueDateAlert, additionalChargeId } = values;

                showGlobalOverlay({ type: 'LoadingOverlay' });
                setIsSaving(true);

                // map form values to payload
                const payload: UpdateInvoiceConfigGeneralSettingsPayload = {
                    invoiceDueDays,
                    dueDateAlert,
                    additionalCharge: {
                        id: additionalChargeId,
                    },
                };

                await updateGeneralSettings(payload);

                setIsEditing(false);

                Notification({
                    type: 'success',
                    messageTitle: 'Update Complete',
                    description: 'Successfully updated configuration.',
                });
            } catch (error) {
                const errorMessage = getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE;
                Notification({
                    type: 'error',
                    messageTitle: 'Update Failed',
                    description: errorMessage,
                });
            } finally {
                setIsSaving(false);
                hideGlobalOverlay();
            }
        },
    });

    // Ant Design needs the selected option present in the options array to render its label.
    // We seed it from config so we don't need a separate request, but only inject it when
    // it is the current selection and no search query is active — otherwise it shows up as
    // a non-matching result while the user types.
    const additionalChargesOptions: ChargeSelectOption[] = useMemo(() => {
        const listOptions =
            data?.list?.map((el) => ({
                id: el.id,
                value: el.id,
                rawData: el,
                label: <AdditionalChargeLabel charge={el} />,
            })) ?? [];

        const configCharge = config?.additionalCharge;
        const isSelected = configCharge?.id === formik.values.additionalChargeId;

        if (configCharge && isSelected && !debouncedQuery && !listOptions.some((o) => o.id === configCharge.id)) {
            return [
                {
                    id: configCharge.id,
                    value: configCharge.id,
                    rawData: configCharge,
                    label: <AdditionalChargeLabel charge={configCharge} />,
                },
                ...listOptions,
            ];
        }

        return listOptions;
    }, [data?.list, config?.additionalCharge, formik.values.additionalChargeId, debouncedQuery]);

    useSyncDirty('generalSettings', formik.dirty);

    return (
        <CardWithIcon
            title="General Settings"
            gap="large"
            iconProps={{
                icon: <SettingOutlined />,
                status: 'warning',
            }}
            headerActions={
                <Space size={8}>
                    {isEditing && (
                        <Button
                            type="outlined"
                            disabled={isSaving}
                            onClick={() => {
                                formik.resetForm();
                                setIsEditing(false);
                            }}
                        >
                            Cancel
                        </Button>
                    )}
                    <Button
                        type="outlined"
                        loading={isSaving}
                        onClick={isEditing ? formik.submitForm : () => setIsEditing(true)}
                    >
                        {isSaving ? 'Saving' : isEditing ? 'Save' : 'Edit'}
                    </Button>
                </Space>
            }
            children={
                <FormikProvider value={formik}>
                    <div style={{ width: '100%', marginTop: 16 }}>
                        <Space size={24} direction="vertical" style={{ width: '100%' }}>
                            <Space size={8} direction="vertical" style={{ width: '100%' }}>
                                <div>
                                    <H3>TAN Fee</H3>
                                    <Typography.Text>
                                        Set a TAN fee rule that will apply to all teams under your tenant. You can
                                        always add exceptions and set new rules for them.
                                    </Typography.Text>
                                </div>
                                <div>
                                    <label htmlFor="additionalChargeId">
                                        <LabelRequiredSymbol style={{ marginRight: 8 }} />
                                        Select TAN Fee
                                    </label>
                                </div>

                                <div style={{ maxWidth: MAX_INPUT_WIDTH }}>
                                    <FormSelect
                                        hideKeys
                                        disabled={!isEditing}
                                        onSearch={setQuery}
                                        onClear={() => setQuery(undefined)}
                                        style={{ maxWidth: '100%' }}
                                        placeholder="Select TAN Fee"
                                        filterOption={false}
                                        fieldProps={formik.getFieldProps('additionalChargeId')}
                                        fieldMeta={formik.getFieldMeta('additionalChargeId')}
                                        fieldHelper={formik.getFieldHelpers('additionalChargeId')}
                                        selectOptions={additionalChargesOptions as any}
                                        renderOptionContent={(item) => (item as any).label}
                                        optionLabelProp="label"
                                        getOptionLabel={(item) => (
                                            <AdditionalChargeLabel charge={(item as any).rawData} />
                                        )}
                                        dropdownRender={(menu) => <>{menu}</>}
                                    />
                                </div>
                            </Space>

                            <Space size={8} direction="vertical" style={{ width: '100%' }}>
                                <div>
                                    <H3>Due Date</H3>
                                    <Typography.Text>
                                        Set a Due date rule that will apply to all invoices. Due date can be manually
                                        changed during the invoice creation.
                                    </Typography.Text>
                                </div>

                                <div>
                                    <label htmlFor="dueDate">
                                        <LabelRequiredSymbol style={{ marginRight: 8 }} />
                                        Due Date (days)
                                    </label>
                                    <div>
                                        <FormNumberInput
                                            inputMode="numeric"
                                            name="invoiceDueDays"
                                            min={0}
                                            placeholder="Enter due date"
                                            max={365 * 5}
                                            style={{ maxWidth: MAX_INPUT_WIDTH, marginTop: 6 }}
                                            parser={(value) => value?.replace(/\.\d*/g, '') || ''} // Disable decimal values
                                            disabled={!isEditing}
                                        />
                                    </div>
                                </div>
                            </Space>

                            <Space size={8} direction="vertical" style={{ width: '100%' }}>
                                <div>
                                    <H3>Due Date Alerts</H3>
                                    <Typography.Text>
                                        Set up a custom reminder to notify you before an invoice is due.
                                    </Typography.Text>
                                </div>

                                <div>
                                    <label htmlFor="dueDate">
                                        <LabelRequiredSymbol style={{ marginRight: 8 }} />
                                        Due Date Alert
                                    </label>
                                    <div
                                        style={{
                                            maxWidth: MAX_INPUT_WIDTH,
                                        }}
                                    >
                                        <DueDateAlertsField disabled={!isEditing} />
                                    </div>
                                </div>
                            </Space>
                        </Space>
                    </div>
                </FormikProvider>
            }
        />
    );
};

export default GeneralSettingsSection;
